import api from '@common/api';
import config from '@common/config';
import params from '@common/params';
import { AgreementCode, InvestorAccountApplicationInput } from '@services/appsync/types';
import { JwtPayload } from '@aws-amplify/core/internals/utils';
import { UseFormReturn } from 'react-hook-form';
import create from 'zustand';
import { CreateAccountForm, CreateAccountState } from '../types';
import auth from '@common/auth';

interface CreateAccountViewModelProps {
  state: CreateAccountState;
}

interface CreateAccountViewModelActions {
  setCreateAccountState: (state: CreateAccountState) => void;
  createInvestorAccount: (
    form: UseFormReturn<CreateAccountForm>,
  ) => Promise<{ success: boolean; error?: string; investorId?: string }>;
}

function normalizeSplitNameFromUser(user: JwtPayload): { firstName: string; lastName: string } {
  if (user.name) {
    // Split the name, then set the first name to the first part, and the rest to the last name.
    const parts = (user.name as string).split(' ');
    const firstName = parts.slice(0, parts.length - 1).join(' ');
    const lastName = parts.slice(parts.length - 1).join(' ');
    return { firstName, lastName };
  } else {
    return { firstName: user.given_name as string, lastName: user.family_name as string };
  }
}

export const useCreateAccountViewModel = create<CreateAccountViewModelProps & CreateAccountViewModelActions>(
  (set, get) => {
    return {
      createInvestorAccount: async (form) => {
        const { address1, address2, citizenship, city, state, zip } = form.getValues();
        const partner = params.getItem('partner');
        const user = (await auth.getCurrentSession()).tokens?.idToken?.payload;
        if (!user) {
          throw new Error('Could not get an authenticated user');
        }
        const normalizedUser = normalizeSplitNameFromUser(user as JwtPayload);

        const investorAppData: InvestorAccountApplicationInput = {
          address: address1?.toUpperCase(),
          ...(address2 !== '' && { address2: address2?.toUpperCase() }),
          city: city,
          country: 'US',
          domicile: citizenship,
          email: user?.email as string | undefined,
          individual_details: {
            account_type: 'INDIVIDUAL',
            first_name: normalizedUser.firstName,
            last_name: normalizedUser.lastName,
          },
          phone: user?.phone_number as string | undefined,
          state: state,
          zip: zip,
        };

        if (partner) {
          investorAppData.partner_id = partner;
        }

        // first: create investor account application
        const investorAppId = await api.createOrUpdateInvestorAccountApplication({
          investor_account: investorAppData,
        });

        if (!investorAppId) {
          return { error: 'Something has gone wrong, please try again.', success: false };
        }

        // second: create investor account
        const isApproved = await api.approveInvestorAccountApplication({
          agreements: [AgreementCode.ADVISORY, AgreementCode.DWOLLA_TOU_PRIVACY, AgreementCode.TOU_PRIVACY],
          application_id: investorAppId,
          pricing_plan: config.CURRENT_PRICING_PLAN,
        });

        if (!isApproved) {
          return { error: 'Something has gone wrong, please try again.', success: false };
        }

        // cleanup
        params.deleteItem('partner');

        return { investorId: investorAppId, success: true };
      },
      setCreateAccountState: async (state: CreateAccountState) => {
        set({ state });
      },
      state: 'CITIZENSHIP',
    };
  },
);
